import React, { type FC, useEffect, useMemo, useRef, useState } from "react";
import notification from "@app/services/notification";
import { useScannerRegistration, type ScannerHintConfig, type ScannerRegistrationConfig } from "@app/scanner";
import { BusinessForm, type FormHandle } from "@src/components/business-form";
import { StorePlaceCellPickerField } from "@src/businessModules/Stock/components/StorePlaceCellPickerField/StorePlaceCellPickerField";
import { Input } from "@shared/ui/input";
import FormFooter from "@shared/ui/form-footer";
import { FormPanel, type FormPanelRenderProps } from "@shared/ui/form-panel";
import { DetailList } from "@shared/ui/detail-row";
import { EmptyState } from "@shared/ui/empty-state";
import {
    BusinessMeasureValue,
    BusinessStockObjValue,
    formatLotText,
    formatStockObjText,
} from "@src/components/business-data-display";
import { measureEndDecorator } from "@src/helpers/business/measureEndDecorator";
import {
    groupSpMovingsByCell,
    sumSpMovingQty,
    type SpMovingCellGroup,
    type StockPlacementContent,
} from "../../../../../../api/storePlaces";
import type { UseStockPlacementCardMutationsResult } from "../../useStockPlacementCardMutations";
import type { ConfirmStockPlacementCancelParams } from "../../useStockPlacementCancelConfirm";
import { useStockPlacementContentsQuery } from "../../hooks/useStockPlacementContentsQuery";
import {
    stockPlacementContentFormConfig,
    type StockPlacementContentFormContext,
    type StockPlacementContentFormValues,
} from "./stockPlacementContent.form-config";
import { resolveStorePlaceCellScan } from "./resolveStorePlaceCellScan";
import "./StockPlacementContentForm.scss";
import {
    formatCellName,
    formatPlacedCellsFromSpMovings,
    formatRecommendedCells,
} from "@src/businessModules/Stock/modules/StockPlacement/modules/Card/components/StockPlacementContentsList/StockPlacementContentsList";

const CELL_SCAN_HINT: ScannerHintConfig = {
    stateKey: "stock-placement.content.scan-cell",
    title: "Сканируйте номер объекта ячейки для размещения",
    description: "Найдём конечную ячейку склада и выберем её для размещения.",
    position: { placement: "bottom-left", offset: { y: -40 } },
    initialCollapsed: true,
    indicator: { variant: "scanner" },
};

/** Пропсы компонента {@link StockPlacementContentForm}. */
export interface StockPlacementContentFormProps {
    /** Код приходного ордера, которому принадлежит строка состава. */
    orderCode: number;
    /** Код строки состава приходного ордера. */
    contentCode: number;
    /** Код склада приходного ордера для загрузки ячеек. */
    divisionCode: number;
    /** Мутация закрепления партии в ячейке. */
    placeLotMutation: UseStockPlacementCardMutationsResult["placeLotMutation"];
    /**
     * Подтверждение и отмена размещения в ячейке.
     * @param params параметры отмены с полями для confirm
     */
    confirmAndCancelPlacement: (params: ConfirmStockPlacementCancelParams) => Promise<void>;
    /**
     * Признак выполнения отмены размещения.
     *
     * TODO(overlay): снимок из `windows.open` — `cancelPlacementMutation.isPending` с карточки
     * не обновляется в окне. Сейчас частично спасает локальный `cancelingCellCode`.
     * Пересмотреть: `useIsMutating({ mutationKey })` в форме или локальный pending вокруг cancel.
     */
    isCancelPlacementPending: boolean;
}

/** Форма размещения строки состава приходного ордера. */
export const StockPlacementContentForm: FC<StockPlacementContentFormProps> = ({
    orderCode,
    contentCode,
    divisionCode,
    placeLotMutation,
    confirmAndCancelPlacement,
    isCancelPlacementPending,
}) => {
    const contentsQuery = useStockPlacementContentsQuery(orderCode, { refetchOnMount: false });
    const content = contentsQuery.content.find((item) => item.Code === contentCode);

    if (content == null) {
        return <EmptyState text="Строка размещения не найдена" />;
    }

    return (
        <StockPlacementContentFormBody
            content={content}
            divisionCode={divisionCode}
            placeLotMutation={placeLotMutation}
            confirmAndCancelPlacement={confirmAndCancelPlacement}
            isCancelPlacementPending={isCancelPlacementPending || contentsQuery.isFetching}
        />
    );
};

interface StockPlacementContentFormBodyProps {
    content: StockPlacementContent;
    divisionCode: number;
    placeLotMutation: UseStockPlacementCardMutationsResult["placeLotMutation"];
    confirmAndCancelPlacement: (params: ConfirmStockPlacementCancelParams) => Promise<void>;
    isCancelPlacementPending: boolean;
}

const StockPlacementContentFormBody: FC<StockPlacementContentFormBodyProps> = ({
    content,
    divisionCode,
    placeLotMutation,
    confirmAndCancelPlacement,
    isCancelPlacementPending,
}) => {
    const formApiRef = useRef<FormHandle<StockPlacementContentFormValues> | null>(null);
    const [cancelingCellCode, setCancelingCellCode] = useState<number | null>(null);
    const placedQty = sumSpMovingQty(content.SpMovings);
    const remainingQty = Math.max(content.DepotQty - placedQty, 0);
    const isFullyPlaced = remainingQty === 0;

    const formContext = useMemo(
        (): StockPlacementContentFormContext => ({
            remainingQty,
        }),
        [remainingQty],
    );

    const boundFormConfig = useMemo(() => stockPlacementContentFormConfig.withContext(formContext), [formContext]);

    /** Снимок остатка на момент открытия формы; дальше значение догоняет эффект ниже. */
    const mountRemainingQtyRef = useRef(remainingQty);
    const initialValues = useMemo(
        (): StockPlacementContentFormValues => ({
            cellCode: null,
            placeQty: mountRemainingQtyRef.current,
        }),
        [],
    );

    /**
     * Остаток к размещению меняется мутациями в этом же окне, а RHF не следит за `initialValues`
     * после маунта. Догоняем форму вручную: `keepDirtyValues` сохраняет вручную введённое
     * количество и выбранную ячейку, обновляются только не тронутые пользователем поля.
     */
    useEffect(() => {
        formApiRef.current?.reset({ cellCode: null, placeQty: remainingQty }, { keepDirtyValues: true, keepErrors: true });
    }, [remainingQty]);

    const handleSubmit = async (values: StockPlacementContentFormValues): Promise<void> => {
        if (isFullyPlaced) {
            return;
        }

        if (!isValidCode(content.LotCode)) {
            notification.warning("Для размещения у строки должна быть задана партия.");
            return;
        }

        if (values.cellCode == null) {
            return;
        }

        await placeLotMutation.mutateAsync({
            lotCode: content.LotCode,
            cellCode: values.cellCode,
            qty: values.placeQty,
            contentCode: content.Code,
        });
    };

    const handleCancelPlacement = async (placedCell: SpMovingCellGroup): Promise<void> => {
        if (!isValidCode(content.LotCode)) {
            notification.warning("Для отмены размещения у строки должна быть задана партия.");
            return;
        }

        const cellLabel = placedCell.StorePlace?.Num;

        setCancelingCellCode(placedCell.SpCode);
        try {
            await confirmAndCancelPlacement({
                lotCode: content.LotCode,
                cellCode: placedCell.SpCode,
                contentCode: content.Code,
                fields: [
                    { label: "Ячейка", value: cellLabel },
                    { label: "Партия", value: formatLotText(content.Lot) },
                    { label: "Номенклатура", value: formatStockObjText(content.StockObj) },
                ],
            });
        } finally {
            setCancelingCellCode(null);
        }
    };

    const placedCells = groupSpMovingsByCell(content.SpMovings);

    return (
        <FormPanel<StockPlacementContentFormValues>
            formApiRef={formApiRef}
            useDefaultFooter={false}
            disabled={isFullyPlaced}
            formProps={{
                formConfig: boundFormConfig,
                initialValues,
                ready: true,
                onSubmit: handleSubmit,
            }}
            footer={({ submitForm, isFormSubmitting }) => (
                <FormFooter
                    confirmText="Разместить"
                    confirmLoading={isFormSubmitting}
                    confirmDisabled={isFullyPlaced}
                    onConfirm={submitForm}
                />
            )}
        >
            {(form) => (
                <div className="stock_placement_content_form">
                    <StockPlacementContentScanRegistration
                        form={form}
                        divisionCode={divisionCode}
                        showHint={!isFullyPlaced}
                        paused={
                            isFullyPlaced ||
                            form.isFormSubmitting === true ||
                            isCancelPlacementPending ||
                            cancelingCellCode != null
                        }
                    />
                    <DetailList layout="tiles" columns="auto" minWidth={112}>
                        <DetailList.Row label="Номенклатура" value={<BusinessStockObjValue stockObj={content.StockObj} />} />
                        <DetailList.Row
                            className={isFullyPlaced ? "stock_placement_content_form__placed_status" : undefined}
                            label="Размещено"
                            value={
                                <BusinessMeasureValue
                                    value={`${placedQty} / ${content.DepotQty}`}
                                    measure={content.Measure}
                                />
                            }
                        />
                        <DetailList.Row
                            label="Рекомендуемые МХ"
                            value={formatDetailValue(formatRecommendedCells(content.RecommendedCells))}
                        />
                        <DetailList.Row
                            label="Фактическое МХ"
                            value={formatDetailValue(formatPlacedCellsFromSpMovings(content.SpMovings))}
                        />
                    </DetailList>

                    <div className="stock_placement_content_form__fields">
                        <BusinessForm.Field name="cellCode">
                            <StorePlaceCellPickerField
                                divisionCode={divisionCode}
                                recommendedCells={content.RecommendedCells}
                                value={form.getValues("cellCode")}
                                onChange={(cellCode) => {
                                    form.setValue("cellCode", cellCode, { shouldDirty: true });
                                }}
                                label={form.field("cellCode").getUiProps().label}
                                required={form.field("cellCode").getUiProps().required}
                                disabled={form.field("cellCode").getUiProps().disabled}
                                error={form.getFieldState("cellCode").error != null}
                            />
                        </BusinessForm.Field>

                        <BusinessForm.Field name="placeQty">
                            <Input
                                type="number"
                                {...form.field("placeQty").getNumberInputProps()}
                                endDecorator={measureEndDecorator(content.Measure)}
                            />
                        </BusinessForm.Field>
                    </div>

                    {placedCells.length > 0 && (
                        <section className="stock_placement_content_form__placed" aria-label="Размещённые остатки">
                            <h3 className="stock_placement_content_form__placed_title">Размещённые остатки</h3>
                            <DetailList layout="tiles" oneLine={true}>
                                {placedCells.map((placedCell) => {
                                    return (
                                        <DetailList.Row
                                            key={placedCell.SpCode}
                                            label={formatCellName(placedCell.StorePlace)}
                                            value={<BusinessMeasureValue value={placedCell.Qty} measure={content.Measure} />}
                                            onClick={() => {
                                                const disabled = isCancelPlacementPending || cancelingCellCode != null;
                                                if (!disabled) {
                                                    void handleCancelPlacement(placedCell);
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </DetailList>
                        </section>
                    )}
                </div>
            )}
        </FormPanel>
    );
};

/** Пропсы компонента {@link StockPlacementContentScanRegistration}. */
interface StockPlacementContentScanRegistrationProps {
    /** API формы размещения строки состава. */
    form: FormPanelRenderProps<StockPlacementContentFormValues>;
    /** Код склада приходного ордера. */
    divisionCode: number;
    /**
     * Показывать ли app-level hint сканирования ячейки. \
     * При полностью размещённой строке hint скрыт, но registration остаётся topmost, \
     * чтобы не всплывала подсказка карточки («сканируйте партию»).
     *
     * TODO(scanner): архитектурный нюанс — «скрыть hint, но удержать topmost» сейчас
     * делается через `hint: undefined` + `paused` при живой window-регистрации. \
     * Нужен явный контракт registry (например `holdWithoutHint` / suppress-below),
     * а не этот обходной паттерн в каждом feature-экране.
     */
    showHint: boolean;
    /**
     * Временная пауза scanner-сценария (мутации / полностью размещено). \
     * Регистрация остаётся topmost, hint формы не заменяется hint карточки.
     */
    paused: boolean;
}

/** Регистрирует сценарий сканирования ячейки внутри окна размещения строки. */
const StockPlacementContentScanRegistration: FC<StockPlacementContentScanRegistrationProps> = ({
    form,
    divisionCode,
    showHint,
    paused,
}) => {
    const handleScan = React.useCallback(
        async (barcode: string): Promise<void> => {
            const result = await resolveStorePlaceCellScan(barcode, divisionCode);

            if (result.type === "notFound") {
                notification.warning(`Ячейка размещения для скана "${barcode}" не найдена на складе.`);
                return;
            }

            if (result.type === "ambiguous") {
                notification.warning(`Для скана "${barcode}" найдено несколько ячеек. Выберите ячейку вручную.`);
                return;
            }

            form.setValue("cellCode", result.cell.Code, { shouldDirty: true });
            notification.success(`Ячейка "${result.cell.Num}" выбрана для размещения.`);
        },
        [divisionCode, form],
    );

    const scannerRegistration = React.useMemo(
        (): ScannerRegistrationConfig => ({
            paused,
            priority: "window",
            captureInEditable: true,
            // TODO(scanner): hide hint without yielding topmost — see showHint JSDoc; нужен shared API.
            hint: showHint ? CELL_SCAN_HINT : undefined,
            onScan: handleScan,
            errorSource: "stock.placement.content.scanCell",
        }),
        [handleScan, paused, showHint],
    );

    useScannerRegistration(scannerRegistration);

    return null;
};

function formatDetailValue(value: string): string {
    return value === "" ? "Не задано" : value;
}

function isValidCode(code: number): boolean {
    return code !== 0;
}
